import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Zap, Target, ExternalLink } from 'lucide-react';
import { companyInfo, products } from '../mockData';

const Home = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-0">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="stars-container">
            {[...Array(60)].map((_, i) => (
              <div
                key={i}
                className="star"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>
          
          <div className="absolute inset-0 bg-gradient-radial from-white/[0.03] via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-32 text-center">
          <div className="mb-10 animate-fade-in">
            <img 
              src="/images/nrfstr-logotext.png" 
              alt="NRFSTR" 
              className="h-32 md:h-40 mx-auto mb-10 opacity-95"
            />
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-light mb-7 tracking-tight animate-fade-in-up leading-[1.1]">
            Building the
            <br />
            <span className="font-normal bg-gradient-to-r from-white via-white/95 to-white/80 bg-clip-text text-transparent">
              future
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-14 leading-relaxed animate-fade-in-up animation-delay-200">
            Innovative digital products and software that empower creators and transform workflows
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
            <Link
              to="/products"
              className="group px-9 py-4 bg-white text-black rounded-full font-medium hover:bg-white/95 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg shadow-white/10"
            >
              <span>Explore Products</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/about"
              className="px-9 py-4 bg-white/[0.06] text-white rounded-full font-medium hover:bg-white/[0.10] backdrop-blur-sm border border-white/[0.12] transition-all duration-300"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-7 tracking-tight">
              Our Products
            </h2>
            <p className="text-white/55 text-lg max-w-2xl mx-auto leading-relaxed">
              Innovative solutions designed to empower creators and streamline workflows
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="group relative bg-gradient-to-b from-white/[0.04] to-white/[0.01] rounded-[2rem] p-8 border border-white/[0.08] hover:border-white/[0.15] transition-all duration-500 overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Hover Gradient Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/[0.03] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  {/* Logo */}
                  <div className="mb-7 h-14 flex items-center">
                    {product.logo && (
                      <img 
                        src={product.logo} 
                        alt={product.name} 
                        className="h-14 w-auto opacity-95 group-hover:opacity-100 transition-opacity"
                      />
                    )}
                  </div>

                  {/* Badge */}
                  <div className="flex items-center gap-2 mb-6">
                    {product.comingSoon ? (
                      <span className="px-3 py-1.5 bg-white/[0.08] text-white/65 text-xs font-medium rounded-full border border-white/[0.12]">
                        Coming Soon
                      </span>
                    ) : (
                      <span className="px-3 py-1.5 bg-green-500/10 text-green-400 text-xs font-medium rounded-full border border-green-500/20">
                        Available Now
                      </span>
                    )}
                  </div>
                  
                  <p className="text-white/45 text-sm mb-6 leading-relaxed">{product.tagline}</p>
                  
                  <p className="text-white/70 leading-relaxed mb-8 text-[15px]">
                    {product.description}
                  </p>

                  {/* Availability Info */}
                  <div className="mb-8 p-4 bg-white/[0.03] rounded-2xl border border-white/[0.06]">
                    <p className="text-white/55 text-sm leading-relaxed">
                      {product.availability}
                    </p>
                  </div>
                  
                  {product.link ? (
                    <a
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-white/75 hover:text-white group-hover:translate-x-1 transition-all duration-300"
                    >
                      <span className="text-sm font-medium">Visit Site</span>
                      <ExternalLink size={16} className="ml-2" />
                    </a>
                  ) : (
                    <Link
                      to={`/products#${product.id}`}
                      className="flex items-center text-white/75 hover:text-white group-hover:translate-x-1 transition-all duration-300"
                    >
                      <span className="text-sm font-medium">Learn more</span>
                      <ArrowRight size={16} className="ml-2" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 relative border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-7 tracking-tight">
              Why NRFSTR
            </h2>
            <p className="text-white/55 text-lg max-w-2xl mx-auto leading-relaxed">
              Our approach to building exceptional products
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                icon: <Sparkles size={30} />,
                title: "Innovation First",
                description: "We push boundaries and explore new possibilities in every product we build."
              },
              {
                icon: <Zap size={30} />,
                title: "Speed & Clarity",
                description: "Our products are designed for efficiency without sacrificing simplicity or elegance."
              },
              {
                icon: <Target size={30} />,
                title: "Purpose-Driven",
                description: "Every feature serves a purpose. We build tools that solve real problems."
              }
            ].map((value, index) => (
              <div
                key={index}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/[0.04] text-white/75 mb-7 group-hover:bg-white/[0.08] group-hover:text-white transition-all duration-300 border border-white/[0.08]">
                  {value.icon}
                </div>
                <h3 className="text-xl font-medium mb-4">{value.title}</h3>
                <p className="text-white/55 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-7 tracking-tight">
            Ready to explore?
          </h2>
          <p className="text-white/55 text-lg mb-14 leading-relaxed">
            Discover how NRFSTR products can transform your workflow
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-9 py-4 bg-white text-black rounded-full font-medium hover:bg-white/95 transition-all duration-300 group shadow-lg shadow-white/10"
          >
            <span>Get in Touch</span>
            <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
